import {CommonModule, Location} from '@angular/common';
import {Component} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FeatureFlagService, ToastService, TranslationService} from '@emma-labs/shared/angular';
import {
  CategoryCode,
  ErrorResponseCode,
  FeatureFlags,
  getTotalCoverage,
  HUMANIA_EMMA_V2_PROVIDER,
  PersonInsurance,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ConfirmationService} from 'primeng/api';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ToastModule} from 'primeng/toast';
import {finalize, first, Observable, of, tap} from 'rxjs';
import {Client} from '../../entities/proposal-page-object/client';
import {ProposalPageObject, SectionCompleted} from '../../entities/proposal-page-object/proposal-page-object';
import {ProposalPageOrchestrator} from '../../services/proposal/proposal-page.orchestrator';
import {MainComponent} from '../main/main.component';
import {SkeletonComponent} from '../main/skeleton/skeleton.component';
import {AddressSectionComponent} from './address-section/address-section.component';
import {BeneficiariesSectionComponent} from './beneficiaries-section/beneficiaries-section.component';
import {ClientSectionComponent} from './client-section/client-section.component';
import {CoverageBuilderSectionComponent} from './coverage-builder-section/coverage-builder-section.component';
import {DisclosuresSectionComponent} from './disclosures-section/disclosures-section.component';
import {ExtraQuestionnairesSectionComponent} from './extra-questionnaires-section/extra-questionnaires-section.component';
import {FnaSectionComponent} from './fna-section/fna-section.component';
import {HealthQuestionnaireSectionComponent} from './health-questionnaire-section/health-questionnaire-section.component';
import {IdDocumentSectionComponent} from './id-document-section/id-document-section.component';
import {NoteSectionComponent} from './note-section/note-section.component';
import {ProtectionBuilderSectionComponent} from './protection-builder-section/protection-builder-section.component';
import {ReplacementNoticeSectionComponent} from './replacement-notice-section/replacement-notice-section.component';

@UntilDestroy()
@Component({
  selector: 'em-proposal-page',
  templateUrl: './proposal-page.component.html',
  imports: [
    CommonModule,
    CardModule,
    MainComponent,
    ClientSectionComponent,
    FnaSectionComponent,
    HealthQuestionnaireSectionComponent,
    ExtraQuestionnairesSectionComponent,
    BeneficiariesSectionComponent,
    ReplacementNoticeSectionComponent,
    AddressSectionComponent,
    IdDocumentSectionComponent,
    DisclosuresSectionComponent,
    ButtonModule,
    ToastModule,
    SkeletonComponent,
    CoverageBuilderSectionComponent,
    ConfirmDialogModule,
    NoteSectionComponent,
    ProtectionBuilderSectionComponent,
  ],
})
export class ProposalPageComponent {
  proposalPageObject$!: Observable<ProposalPageObject>;
  proposalId: string | undefined;
  provider = HUMANIA_EMMA_V2_PROVIDER;
  isSendButtonLoading = false;
  isCopyToBrokerageButtonLoading = false;
  isSendToBrokerageEnabled = false;
  isNewCoverageBuilderEnabled = false;

  constructor(
    private readonly orchestrator: ProposalPageOrchestrator,
    private readonly toastService: ToastService,
    private readonly confirmationService: ConfirmationService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly translate: TranslationService,
    private readonly location: Location,
    private readonly router: Router,
    route: ActivatedRoute,
  ) {
    if (route.snapshot.params['proposalId']) {
      this.proposalId = atob(route.snapshot.params['proposalId']);
    }

    this.setProposalPageObject$();

    this.featureFlagService
      .isEnabled(FeatureFlags.IS_SEND_FROM_EMMA_PRO_TO_BROKERAGE)
      .pipe(first())
      .subscribe((isEnabled) => {
        this.isSendToBrokerageEnabled = isEnabled;
      });

    this.featureFlagService
      .isEnabled(FeatureFlags.IS_NEW_COVERAGE_BUILDER_ENABLED)
      .pipe(first())
      .subscribe((isEnabled) => {
        this.isNewCoverageBuilderEnabled = isEnabled;
      });
  }

  createOrUpdateProposal(event: {output: Client; callback?: (status: boolean) => void}) {
    if (this.proposalId) {
      this.orchestrator
        .update({client: event.output})
        .pipe(first())
        .subscribe({
          error: (error) => this.handleError(error.code),
        });
      return;
    }

    this.orchestrator
      .create(this.provider.id, event.output)
      .pipe(first())
      .subscribe({
        next: (proposalPageObject) => {
          this.proposalId = proposalPageObject.proposalId;
          this.location.replaceState(`/dashboard/proposal/${btoa(this.proposalId!)}`);
          this.proposalPageObject$ = this.orchestrator.get(proposalPageObject.proposalId!);
          this.setProposalPageObject$();
        },
        error: (error) => {
          this.handleError(error.code);
          if (event.callback) {
            event.callback(false);
          }
        },
      });
  }

  update(
    event: {output: unknown; callback?: (status: boolean) => void},
    propertyName: keyof ProposalPageObject,
  ) {
    this.orchestrator
      .update({[propertyName]: event.output})
      .pipe(first())
      .subscribe({
        next: () => {
          if (event.callback) {
            event.callback(true);
          }
        },
        error: (error) => {
          this.handleError(error.code);
          if (event.callback) {
            event.callback(false);
          }
        },
      });
  }

  displayProtectionResetToaster() {
    this.toastService.warn(
      this.translate.currentLanguageIsFr()
        ? 'La protection a été réinitialisée.'
        : 'The protection has been reset.',
    );
  }

  send() {
    this.isSendButtonLoading = true;

    this.orchestrator
      .send()
      .pipe(
        first(),
        finalize(() => (this.isSendButtonLoading = false)),
      )
      .subscribe({
        next: () => {
          this.toastService.success(
            this.translate.currentLanguageIsFr()
              ? 'La proposition a été envoyée!'
              : 'The proposal has been sent!',
          );
        },
        error: (error) => this.handleError(error.code),
      });
  }

  delete() {
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      header: this.translate.currentLanguageIsFr() ? 'Supprimer la proposition' : 'Delete the proposal',
      message: this.translate.currentLanguageIsFr()
        ? 'Vous êtes sur le point de supprimer la proposition. Veuillez confirmer en cliquant sur le bouton confirmer.'
        : 'You are about to delete the proposal. Please confirm by clicking on the confirm button.',
      acceptLabel: this.translate.currentLanguageIsFr() ? 'Confirmer' : 'Confirm',
      rejectLabel: this.translate.currentLanguageIsFr() ? 'Annuler' : 'Cancel',
      rejectButtonStyleClass: 'reject-btn',
      acceptIcon: 'pi',
      rejectIcon: 'pi',
      accept: () => {
        this.orchestrator
          .delete()
          .pipe(first())
          .subscribe({
            next: () => {
              this.router.navigate(['/', 'dashboard', 'proposals']);
            },
            error: (error) => this.handleError(error.code),
          });
      },
    });
  }

  copyToBrokerage() {
    this.isCopyToBrokerageButtonLoading = true;

    this.orchestrator
      .copyToBrokerage()
      .pipe(
        first(),
        finalize(() => (this.isCopyToBrokerageButtonLoading = false)),
      )
      .subscribe({
        next: () => {
          this.toastService.success(
            this.translate.currentLanguageIsFr()
              ? 'La proposition sera copiée sur la plateforme de courtage sous peu.'
              : 'The proposal will soon be copied on the brokerage platform.',
          );
        },
        error: (error) => this.handleError(error.code),
      });
  }

  isEligible(categoryCode: CategoryCode | undefined): boolean {
    return !!categoryCode && categoryCode !== CategoryCode.INELIGIBLE;
  }

  totalCoverage(personInsurances: PersonInsurance[] | undefined): number {
    if (!personInsurances) {
      return 0;
    }
    return getTotalCoverage(personInsurances);
  }

  clientFullName(client: Client): string {
    return `${client.firstName} ${client.lastName}`;
  }

  isComplete(sectionCompleted: SectionCompleted): boolean {
    return !Object.values(sectionCompleted).some((isComplete) => !isComplete);
  }

  setCurrentProtectionSearchResult(event: {output: string; callback?: (status: boolean) => void}): void {
    this.orchestrator
      .setCurrentProtectionSearchResult(event.output)
      .pipe(first())
      .subscribe({
        error: () => {
          const errorMessage = this.translate.currentLanguageIsFr()
            ? 'La recherche de la protection a échoué.'
            : 'Failed to find the protection.';
          event?.callback?.(false);
          this.toastService.error(errorMessage);
        },
      });
  }

  private setProposalPageObject$(): void {
    if (!this.proposalId) {
      this.proposalPageObject$ = of({sectionCompleted: {} as {[K in keyof SectionCompleted]: false}});
      return;
    }

    this.proposalPageObject$ = this.orchestrator.get(this.proposalId).pipe(
      tap((proposalPageObject) => {
        if (proposalPageObject.isProtectionReset) {
          this.displayProtectionResetToaster();
        }
      }),
      untilDestroyed(this),
    );
  }

  private handleError(errorCode?: ErrorResponseCode) {
    const isFr = this.translate.currentLanguageIsFr();

    switch (errorCode) {
      case ErrorResponseCode.FORBIDDEN: {
        this.toastService.error(
          isFr
            ? `La proposition ne peut qu'être altérée par un agent associé à celle-ci.`
            : 'The proposal can only be altered by an agent associated to it.',
        );
        break;
      }
      case ErrorResponseCode.NOT_FOUND: {
        this.toastService.error(isFr ? `La proposition n'existe pas.` : 'The proposal does not exist.');
        break;
      }
      case ErrorResponseCode.INVALID_BENEFICIARY:
      case ErrorResponseCode.INVALID_TRUSTEE: {
        this.toastService.error(
          isFr
            ? 'Une erreur est survenue lors de la sauvegarde des bénéficiaires.'
            : 'An error occured while saving the beneficiaries.',
        );
        break;
      }
      case ErrorResponseCode.MISSING_POLICY_REPLACEMENT:
        this.toastService.error(
          isFr
            ? 'La proposition doit inclure un avis de remplacement.'
            : 'The proposal must include a replacement notice.',
        );
        break;
      case ErrorResponseCode.INVALID_CURRENT_PROTECTION:
        this.toastService.error(
          isFr
            ? "Une protection actuelle dans l'ABF est invalide."
            : 'A current protection in the FNA is invalid.',
        );
        break;
      case ErrorResponseCode.INVALID_PROPOSAL_AGENT: {
        this.toastService.error(
          isFr
            ? 'Une erreur est survenue lors de la sauvegarde des conseillers.'
            : `An error occured while saving the advisors.`,
        );
        break;
      }
      case ErrorResponseCode.INVALID_HUMANIA_CODE: {
        this.toastService.error(
          isFr ? `Le code n'est associé à aucun conseiller.` : 'The code is not associated to any advisor.',
        );
        break;
      }
      case ErrorResponseCode.MISSING_PROVINCE_PERMIT: {
        this.toastService.error(
          isFr
            ? `Au moins un des conseillers dans la proposition n'a pas de permis de vente dans cette province.`
            : 'At least one of the advisors in this proposal does not have the license to sell in this province.',
        );
        break;
      }
      // The following error codes occur often due to the nature of their entry. As such, we do not want to display an error message for them.
      case ErrorResponseCode.INVALID_EMAIL:
      case ErrorResponseCode.INVALID_PHONE_NUMBER:
      case ErrorResponseCode.INVALID_POSTAL_CODE:
        break;
      default: {
        this.toastService.error(isFr ? 'Une erreur est survenue.' : 'An error occured.');
      }
    }
  }
}
